import { useContext, useState, useEffect, useCallback, useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import "./header.sass";

import { useMeContext } from "@/util/me-context";
import ChildContext from "../../context/child";
import ModalEnableGroupTexting from "../ModalEnableGroupTexting";
import FeedbackSurvey from "../FeedbackSurvey";
import ModalWindow from "../ModalWindow";
import { enableNotifications } from "../../util/fcm-notification-helper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleRight, faArrowRightFromBracket, faGear } from "@fortawesome/pro-light-svg-icons";
import { Box, Button, IconButton, ThemeProvider, Typography, alpha } from "@mui/material";
import MobileHeader from "../Navigation/MobileHeader";
import { Widget } from "@typeform/embed-react";

import { differenceInCalendarDays, getWeekOfMonth } from "date-fns";
import { Notification } from "../NotificationCenter";
import { ContactPhoto } from "../ContactPhoto";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { pinwheelPalette } from "@/styles/theme";
import { useLocalStorage } from "@/hooks/use-localstorage";
import WelcomeScheduleModal from "../WelcomeScheduleModal";
import CancelModal from "../WelcomeScheduleModal/cancel-modal";
import { useIsPinwheelHomeChild, useIsWatchChild, useChildHasPwCode } from "@/hooks";
import { typographyTheme } from "@/styles/customTheme";
import { useHasCellularSubscription, useIsCellularActivated, useIsSecondaryCaregiver } from "@/routes/account/hooks";
import { deviceType, platform } from "@/util/helper";
import { useAtaWizard } from "@/hooks/useAtaWizard";
import { useTranslation } from "react-i18next";
import { AtaDownloadDialog, APP_STORE_URL, PLAY_STORE_URL } from "@/components/AtaDownloadDialog";
import { DeviceType } from "@/openapi";

interface Props {
  onLogoutPress: () => Promise<void>;
}

interface NPSPayload {
  created_at: string;
  feedback?: boolean;
  dismiss?: boolean;
  feedbackAttempt: number;
  userId: string;
}

const Header = ({
  onLogoutPress,
}: Props) => {
  const { data: me, caregiverData } = useMeContext();
  const { child } = useContext(ChildContext);
  const [open, setOpen] = useState(false);
  const [showEnableGroupTexting, setShowEnableGroupTexting] = useState(false);
  const [isTypeFormWidgetOpen, setNPSWidgetOpen] = useState(false);
  const [isNPSFeedback, setNPSFeedback] = useState(false);
  const [isNewVersion, setIsNewVersion] = useState(
    import.meta.env.VITE_VERSION !== localStorage.getItem("appVersion")
  );
  const [welcomeScheduleModal, setWelcomeScheduleModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [showNPSBanner, setShowNPSBanner] = useState(false);
  const [feedbackAttempt, setFeedbackAttempt] = useState(0);
  const currentDate = useMemo(() => new Date(), []);
  const [visitedSchedulePage, setVisitedSchedulePage] = useLocalStorage("visitedSchedulePage", false);
  const location = useLocation();
  const isWatchChild = useIsWatchChild();
  const hasCellularSubscription = useHasCellularSubscription();
  const isCellularActivated = useIsCellularActivated();
  const isSecondaryCaregiver = useIsSecondaryCaregiver();
  const navigate = useNavigate();
  const isPinwheelHomeChild = useIsPinwheelHomeChild(child);
  const { openAtaWizard, ataSupported } = useAtaWizard(child);
  const childHasPwCode = useChildHasPwCode(child);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const { t } = useTranslation("common");

  const handleDownloadClick = useCallback(() => {
    if (platform === "ios") window.location.href = APP_STORE_URL;
    else if (platform === "android") window.location.href = PLAY_STORE_URL;
    else setShowDownloadDialog(true);
  }, []);

  if (isNewVersion) {
     
    localStorage.setItem("appVersion", import.meta.env.VITE_VERSION!);
    setTimeout(() => {
      setIsNewVersion(false);
    }, 3000);
  }

  const showGroupNPSBanner = useCallback(()=>{
    if (me) {
      const weekNumber = getWeekOfMonth(currentDate);
      const groupNumber = userGroup(me.userId);
      
      if (weekNumber === 1 && groupNumber <=1) {
        setShowNPSBanner(true);
      } else if (weekNumber === 2 && groupNumber <=3 && groupNumber > 1) {
        setShowNPSBanner(true);
      } else if (weekNumber === 3 && groupNumber <=5 && groupNumber > 3) {
        setShowNPSBanner(true);
      } else if (weekNumber === 4 && groupNumber <=7 && groupNumber > 5) {
        setShowNPSBanner(true);
      } else if (weekNumber === 5 && groupNumber <=9 && groupNumber > 7) {
        setShowNPSBanner(true);
      }
    }
  }, [me, currentDate]);

  useEffect(() => {
    const checkNPS = localStorage.getItem("stonlyUserData");
    const npsPayload: NPSPayload = checkNPS ? JSON.parse(checkNPS) : {};
    if (!npsPayload.dismiss) {
      if (caregiverData && caregiverData.userCreateDate) {
        if (npsPayload.userId !== me?.attributes.sub) {
          localStorage.removeItem("stonlyUserData");
        }
        const firstLoginDiff = differenceInCalendarDays(currentDate, new Date(caregiverData.userCreateDate));
        const lastFeedbackDiff = differenceInCalendarDays(currentDate, new Date(npsPayload.created_at));        
        setFeedbackAttempt(npsPayload.feedbackAttempt ? npsPayload.feedbackAttempt : 0);
        if (npsPayload.userId === me?.attributes.sub && lastFeedbackDiff > 90) {
          showGroupNPSBanner();          
        } else if (firstLoginDiff > 45 && !npsPayload.userId) {
          showGroupNPSBanner();
        }
      }
    }

  }, [caregiverData, currentDate, me?.attributes.sub, showGroupNPSBanner]);

  // User group by first char
  function userGroup(userId: string): number {
    const groupId =  Array.from(userId).find((e)=>{
      if (Number(e)){
        return e;
      }
    });
    return Number(groupId);
  }

  useEffect(() => {
    // when changes occur in this page during hot reload
    // Firebase App named '[DEFAULT]' already exists, It will occur on hot relado.
    enableNotifications();
  }, []);

  const feedbackNPSWidget = () => {
    setNPSWidgetOpen(true);
  } 
  const dismissNPSWidget = () => {
    const npsDismissData = {
      userId: me?.attributes.sub,
      created_at: currentDate,
      dismiss: true,
    }
    localStorage.setItem("stonlyUserData", JSON.stringify(npsDismissData));
    setShowNPSBanner(false)
  }

  useEffect(() => {
    if (isNPSFeedback) {
      const npsFeedBackDay = {
        userId: me?.attributes.sub,
        created_at: currentDate,
        feedback: true,
        feedbackAttempt: feedbackAttempt + 1
      }
      localStorage.setItem("stonlyUserData", JSON.stringify(npsFeedBackDay));
      setShowNPSBanner(false)
    }
  }, [currentDate, feedbackAttempt, isNPSFeedback, me?.attributes.sub]);

  const handleSetUp = async () => {
    setShowEnableGroupTexting(true);
  };

const shouldShowEnableGroupMessagingPrompt = useMemo(() => {
  if (!child?.deviceInfos) return false;

  const hasCapability = child.deviceInfos.some((deviceInfo) => {
    if (isWatchChild) {
      return !!deviceInfo;
    } else {
      return !!deviceInfo.fcmDeviceToken;
    }
  });

  return hasCapability && !child.configuration.allowGroupMessaging;
}, [child?.configuration.allowGroupMessaging, child?.deviceInfos, isWatchChild]);

  const getHeaderNotice = () => {
    if (!visitedSchedulePage && location.pathname === "/calendar") {
      return (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: "16px",
            "@media (max-width: 414px)": {
              marginBottom: "10px",
              p: {
                fontSize: "14px",
                flex: "1 1 0",
              },
            },
          }}
        >
          <Typography variant="body3">{t("schedulesTutorialVideo")}</Typography>
          <Box>
            <Button
              sx={{ mr: 2 }}
              size="small"
              onClick={() => {
                setWelcomeScheduleModal(true);
                setVisitedSchedulePage(true);
              }}
            >
              {t("watch")}
            </Button>
            <IconButton
              sx={{ width: "24px", height: "24px"}}
              onClick={() => {
                setConfirmModal(true);
              }}
            >
              <FontAwesomeIcon icon={faXmark} size="xs" color={alpha(pinwheelPalette.black[100], 0.75)} />
            </IconButton>
          </Box>
        </Box>
      );
    } else if (isPinwheelHomeChild && !childHasPwCode && !isSecondaryCaregiver) {
      if (ataSupported) {
        return (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: { xs: "10px 25px", md: "0 16px" } }}>
            <Typography variant="body3">{t("activatePinwheelHome")}</Typography>
            <Button onClick={openAtaWizard}>{t("activate")}</Button>
          </Box>
        );
      }
      if (deviceType === DeviceType.Mobile) {
        return (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: { xs: "10px 25px", md: "0 16px" } }}>
            <Typography variant="body3">{t("updateAppToActivate")}</Typography>
          </Box>
        );
      }
      return (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: { xs: "10px 25px", md: "0 16px" } }}>
            <Typography variant="body3">{t("downloadAppToActivate")}</Typography>
            <Button onClick={handleDownloadClick}>{t("downloadTheApp")}</Button>
          </Box>
          <AtaDownloadDialog open={showDownloadDialog} onClose={() => setShowDownloadDialog(false)} />
        </>
      );
    } else if (hasCellularSubscription && !isCellularActivated && !location.pathname.includes("/settings/plan") && !isSecondaryCaregiver && !isPinwheelHomeChild) {
      return (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: { xs: "10px 25px", md: "0 16px" } }}>
          <Typography variant="body3">{t("enableCellular")}</Typography>
          <Button onClick={() => navigate("/settings/plan")}>
            {t("activate")}
          </Button>
        </Box>
      );
    } else if (shouldShowEnableGroupMessagingPrompt && !isPinwheelHomeChild) {
      return (
        <div className="enable-group-texting">
          <Typography variant="body3">{t("enableGroupTexting")}</Typography>
          <Button onClick={handleSetUp}>
            {t("setUp")}
          </Button>
        </div>
      );
    } else if (showNPSBanner) {
      return <FeedbackSurvey dismissNPSWidget={dismissNPSWidget} feedbackNPSWidget={feedbackNPSWidget} />
    } else {
      return null;
    }
  };

  return (
    <ThemeProvider theme={typographyTheme}>
      <div className="dashboard-header-wrapper">
        <div className="dashboard-header">
          <Box className="right" sx={{ display: { xs: "none", lg: "block" } }}>
            <Box sx={{ display: { xs: "none", lg: "block" }, margin: "0 8px" }}>
              <Notification />
            </Box>
            <div className="account">
              <div className="info" data-cy="btnAccountInfo" onClick={() => setOpen(!open)}>
                <FontAwesomeIcon className="account-dropdown-icon" icon={faAngleRight} rotation={open ? 270 : 90} />
                <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                  <Typography variant="body1">{me ? `${me.attributes["given_name"] || ""} ${me.attributes["family_name"] || ""}` : ""}</Typography>
                  <Box sx={{ width: "32px", height: "32px" }}>
                    <ContactPhoto name={me ? `${me.attributes["given_name"] || ""}` : ""} size="small" />
                  </Box>
                </Box>
              </div>
              {open && (
                <div className="account-dropdown">
                  <ul>
                    <li>
                      <Typography variant="body1" component="span">
                        <NavLink style={{ color: pinwheelPalette.black[87] }} onClick={() => setOpen(false)} data-cy="btnAccountSettings" to="/settings/account">
                          <FontAwesomeIcon className="settings-icon" icon={faGear} /> {t("settings")}
                        </NavLink>
                      </Typography>
                    </li>
                    <li className="logout">
                      <Typography variant="body1" color={pinwheelPalette.red[50]} component="span" onClick={onLogoutPress}>
                        <FontAwesomeIcon className="logout-icon" icon={faArrowRightFromBracket} /> {t("logOut")}
                      </Typography>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </Box>
          <Box sx={{ display: { xs: "block", lg: "none" } }}>
            <MobileHeader />
          </Box>
          <ModalWindow
            headerText="Feedback"
            showModal={isTypeFormWidgetOpen}
            setShowModal={setNPSWidgetOpen}
            renderBody={() => (
              <Widget
                id="LzxHvwVm"
                style={{ width: "100%", height: "400px" }}
                className="feedback-form"
                hidden={{
                  email: me?.attributes.email || "",
                }}
                inlineOnMobile
                onSubmit={() => {
                  setNPSWidgetOpen(false);
                  setNPSFeedback(true);
                }}
              />
            )}
            isLarge
            cancelButtonText={t("close")}
          />
          <div className="header-notice-wrapper">{getHeaderNotice()}</div>

          {showEnableGroupTexting && child && !isPinwheelHomeChild ? (
            <ModalEnableGroupTexting isModalShown={showEnableGroupTexting} setShowModal={setShowEnableGroupTexting} child={child} />
          ) : null}

          {!isPinwheelHomeChild ? <WelcomeScheduleModal
            showModal={welcomeScheduleModal}
            setShowModal={(close: boolean) => {
              setWelcomeScheduleModal(close);
            }}
            welcomeModalSteps={1}
          /> : null}
          <CancelModal 
            showModal={confirmModal}
            setShowModal={(close: boolean) => {
              setConfirmModal(close);
            }}
            onActionButtonPress={async () => {
              setConfirmModal(false);
              setVisitedSchedulePage(true);
            }}
          />
        </div>
      </div>
    </ThemeProvider>
  );
};

export default Header;